import {
  QueryErrorResetBoundary,
  useSuspenseQuery,
  type UseSuspenseQueryOptions,
} from '@tanstack/react-query';
import {
  Suspense,
  useRef,
  useState,
  type PropsWithChildren,
  type ReactNode,
} from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage, useIntl } from 'react-intl';

import { ErrorState } from '@/components/ui/error-state';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { reportBoundaryError } from '@/utils/sentry';

interface AggregateBreakdownHoverCardProps<
  TData,
  TRow,
> extends PropsWithChildren {
  query: UseSuspenseQueryOptions<TData, Error, TRow[]>;
  description: ReactNode;
  valueHeader: ReactNode;
  boundary: string;
  renderRow: (row: TRow) => ReactNode;
}

export function AggregateBreakdownHoverCard<
  TData,
  TRow extends { external_id: string },
>({
  children,
  query,
  description,
  valueHeader,
  boundary,
  renderRow,
}: AggregateBreakdownHoverCardProps<TData, TRow>) {
  const [open, setOpen] = useState(false);
  const dismissedRef = useRef(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const intl = useIntl();

  return (
    <HoverCard
      open={open}
      onOpenChange={nextOpen => {
        // Radix can still have an open timer queued when Escape dismisses the card.
        if (nextOpen && dismissedRef.current) return;
        // A pointer-leave timer must not dismiss a keyboard user's card.
        if (
          !nextOpen &&
          ((triggerRef.current === document.activeElement &&
            triggerRef.current?.matches(':focus-visible')) ||
            contentRef.current?.contains(document.activeElement))
        )
          return;
        setOpen(nextOpen);
      }}
    >
      <HoverCardTrigger
        asChild
        onPointerMove={() => {
          // Popup removal can cause pointer entry without a new mouse movement.
          dismissedRef.current = false;
        }}
        onFocus={event => {
          // Avoid a second delayed open when pointer entry also focuses the button.
          event.preventDefault();
          dismissedRef.current = false;
          setOpen(true);
        }}
      >
        <button
          ref={triggerRef}
          type="button"
          className="focus-visible:outline-fx-primary focus-visible:outline-2 focus-visible:outline-offset-2"
          onClick={() => {
            dismissedRef.current = false;
            setOpen(true);
          }}
          onBlur={event => {
            if (contentRef.current?.contains(event.relatedTarget))
              event.preventDefault();
          }}
          onKeyDown={event => {
            // Radix hover cards remove content controls from the tab order.
            const retry = contentRef.current?.querySelector('button');
            if (event.key === 'Tab' && !event.shiftKey && retry) {
              event.preventDefault();
              retry.focus();
            }
          }}
        >
          {children}
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        className="w-80"
        onEscapeKeyDown={() => {
          dismissedRef.current = true;
          setOpen(false);
        }}
      >
        <div
          ref={contentRef}
          onBlur={event => {
            if (
              !event.currentTarget.contains(event.relatedTarget) &&
              event.relatedTarget !== triggerRef.current
            )
              setOpen(false);
          }}
          className="flex flex-col items-start justify-center gap-2 py-1.5"
        >
          <Typography variant="small1" className="!text-fx-neutral-600">
            {description}
          </Typography>
          <div className="flex w-full items-center justify-between py-1.5">
            <Typography variant="caption">
              <FormattedMessage
                defaultMessage="Openings"
                description="Hover table column: linked requisition name"
              />
            </Typography>
            <Typography variant="caption">{valueHeader}</Typography>
          </div>
          <QueryErrorResetBoundary>
            {({ reset }) => (
              <ErrorBoundary
                onError={reportBoundaryError(boundary)}
                onReset={reset}
                resetKeys={[...query.queryKey]}
                fallbackRender={({ resetErrorBoundary }) => (
                  <ErrorState
                    message={intl.formatMessage({
                      defaultMessage:
                        "We couldn't load the opening breakdown. Try again.",
                      description:
                        'Linked opening details failed to load; the employer can retry the request',
                    })}
                    onRetry={() => {
                      // Keep keyboard focus when retry unmounts the error button.
                      triggerRef.current?.focus();
                      resetErrorBoundary();
                    }}
                  />
                )}
              >
                <Suspense fallback={<BreakdownSkeleton />}>
                  <BreakdownRows query={query} renderRow={renderRow} />
                </Suspense>
              </ErrorBoundary>
            )}
          </QueryErrorResetBoundary>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

function BreakdownRows<TData, TRow extends { external_id: string }>({
  query,
  renderRow,
}: Pick<AggregateBreakdownHoverCardProps<TData, TRow>, 'query' | 'renderRow'>) {
  const { data, error, isFetching } = useSuspenseQuery(query);
  if (error && !isFetching) throw error;

  return data.map(row => (
    <div
      key={row.external_id}
      className="flex w-full items-center justify-between py-1.5"
    >
      {renderRow(row)}
    </div>
  ));
}

function BreakdownSkeleton() {
  return [0, 1].map(index => (
    <div
      key={index}
      className="flex w-full items-center justify-between py-1.5"
    >
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-4 w-5" />
    </div>
  ));
}
